<template>
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-2">
      Population Filter
    </label>

    <!-- Quick Presets -->
    <div class="flex flex-wrap gap-2 mb-3">
      <button
        v-for="preset in presets"
        :key="preset.label"
        type="button"
        @click="applyPreset(preset)"
        :class="[
          'btn btn-sm',
          isActivePreset(preset) ? 'btn-primary' : 'btn-secondary'
        ]">
        {{ preset.label }}
      </button>
    </div>

    <!-- Custom Range -->
    <div class="grid grid-cols-2 gap-4">
      <div class="form-group">
        <label class="form-label text-xs">Min Population</label>
        <input
          type="number"
          :value="minPopulation"
          @input="$emit('update:minPopulation', $event.target.value ? parseInt($event.target.value) : null)"
          class="form-input form-input-sm"
          placeholder="0">
      </div>
      <div class="form-group">
        <label class="form-label text-xs">Max Population</label>
        <input
          type="number"
          :value="maxPopulation"
          @input="$emit('update:maxPopulation', $event.target.value ? parseInt($event.target.value) : null)"
          class="form-input form-input-sm"
          placeholder="No limit">
      </div>
    </div>

    <!-- Matching Count -->
    <div v-if="matchingCount > 0 || loading" class="mt-2 text-sm text-gray-600">
      <span v-if="loading" class="flex items-center">
        <svg class="animate-spin h-4 w-4 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Counting...
      </span>
      <span v-else>
        <span class="font-semibold text-blue-600">{{ formatNumber(matchingCount) }}</span> cities match
      </span>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  minPopulation: {
    type: Number,
    default: null
  },
  maxPopulation: {
    type: Number,
    default: null
  },
  matchingCount: {
    type: Number,
    default: 0
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:minPopulation', 'update:maxPopulation', 'apply'])

const presets = [
  { label: 'Major Cities (100k+)', min: 100000, max: null },
  { label: 'Mid-size (25k-100k)', min: 25000, max: 99999 },
  { label: 'Small Towns (10k-25k)', min: 10000, max: 24999 },
  { label: 'Small (5k-10k)', min: 5000, max: 9999 },
  { label: 'All Sizes', min: null, max: null }
]

const isActivePreset = (preset) => props.minPopulation === preset.min && props.maxPopulation === preset.max

const applyPreset = (preset) => {
  emit('update:minPopulation', preset.min)
  emit('update:maxPopulation', preset.max)
  emit('apply')
}

const formatNumber = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
</script>
